import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { formatNumber, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { Person } from '../ui/Avatar';
import { useMembers } from '../ui/UserSelect';
import { accountUsage, type HealthBand } from './api';

/**
 * How this account is being used (context.md §187), for its own admins.
 *
 * Who did something in the last 30 days, which parts of the product the team touches, and which
 * of the first steps are still untaken — the same figures Safe Cortex sees about the account,
 * shown to the account, because a number the customer cannot see is a number they cannot act on.
 */
export function UsagePage(): React.JSX.Element {
  const usage = useQuery({ queryKey: ['account-usage'], queryFn: accountUsage });
  const { options: people } = useMembers();

  if (usage.isLoading) return <Loading />;
  if (usage.error) return <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />;

  const data = usage.data!;
  const peak = Math.max(1, ...data.byDay.map((point) => point.events));

  return (
    <div className="view">
      <PageHeader
        title={t('usage.title')}
        description={t('usage.description').replace('{n}', String(data.windowDays))}
      />

      <div className="metrics">
        <div className="metric">
          <p className="metric__label">{t('usage.activePeople')}</p>
          <p className="metric__value">
            {formatNumber(data.activeUsers)}
            <span className="muted small">{` / ${formatNumber(data.members)}`}</span>
          </p>
        </div>
        <div className="metric">
          <p className="metric__label">{t('usage.activeDays')}</p>
          <p className="metric__value">{formatNumber(data.activeDays)}</p>
        </div>
        <div className="metric">
          <p className="metric__label">{t('usage.health')}</p>
          <p className="metric__value">
            <Badge tone={bandTone(data.health.band)}>{t(`admin.band.${data.health.band}`)}</Badge>
          </p>
          <p className="muted small">
            {t('usage.healthParts')
              .replace('{a}', String(data.health.parts.activity))
              .replace('{b}', String(data.health.parts.breadth))
              .replace('{c}', String(data.health.parts.people))}
          </p>
        </div>
      </div>

      {data.byDay.length > 1 && (
        <Panel title={t('usage.byDay')}>
          <div className="spend-bars" role="img" aria-label={t('usage.byDay')}>
            {data.byDay.map((point) => (
              <div
                key={point.day}
                className="spend-bars__bar"
                style={{ height: `${Math.max(2, (point.events / peak) * 100)}%` }}
                title={`${point.day} · ${formatNumber(point.events)}`}
              />
            ))}
          </div>
        </Panel>
      )}

      <div className="home-grid">
        <Panel title={t('usage.firstSteps')}>
          <ul className="checklist">
            {[
              ...data.activation.reached.map((m) => ({ m, done: true })),
              ...data.activation.missing.map((m) => ({ m, done: false })),
            ].map((step) => (
              <li
                key={step.m}
                className={`checklist__item${step.done ? ' checklist__item--done' : ''}`}
              >
                <span aria-hidden="true">{step.done ? '✓' : '○'}</span>
                <span>{t(`usage.milestone.${step.m}`)}</span>
              </li>
            ))}
          </ul>
          <p className="muted small">
            {data.activation.activated ? t('usage.activated') : t('usage.notActivated')}
          </p>
        </Panel>

        <Panel title={t('usage.areas')}>
          {data.byArea.length === 0 ? (
            <p className="muted">{t('usage.nothingYet')}</p>
          ) : (
            <ul className="funnel">
              {data.byArea.map((row) => (
                <li key={row.area} className="funnel__step">
                  <span className="funnel__label">{t(`usage.area.${row.area}`)}</span>
                  <span
                    className="funnel__bar"
                    style={{
                      width: `${Math.round((row.events / Math.max(1, data.byArea[0]!.events)) * 100)}%`,
                    }}
                  />
                  <span className="funnel__value">
                    {formatNumber(row.events)}{' '}
                    <span className="muted small">
                      {t('usage.byPeople').replace('{n}', String(row.users))}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel title={t('usage.people')}>
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>{t('usage.person')}</th>
                <th className="numeric">{t('usage.actions')}</th>
                <th className="numeric">{t('usage.areasUsed')}</th>
                <th>{t('usage.lastActive')}</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((row) => {
                const found = people.find((person) => person.id === row.id);

                return (
                  <tr key={row.id}>
                    <td>
                      <Person
                        size="xs"
                        name={found?.name ?? row.name}
                        url={found?.avatarUrl ?? null}
                      />
                    </td>
                    <td className="numeric">{formatNumber(row.events)}</td>
                    <td className="numeric">{formatNumber(row.areas)}</td>
                    <td className="muted small">
                      {row.lastActiveAt ? formatRelative(row.lastActiveAt) : t('usage.inactive')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function bandTone(band: HealthBand): BadgeTone {
  return band === 'THRIVING'
    ? 'positive'
    : band === 'HEALTHY'
      ? 'info'
      : band === 'QUIET'
        ? 'warning'
        : band === 'DORMANT'
          ? 'danger'
          : 'neutral';
}
